/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */

import {
    FC,
    $Sprite,
    ContainerProps,
    CanBeSignals,
    $SetVisibility,
    getProp,
    mapProp,
    $Button,
} from '@gongxigames/framework/core/fragments/BuilderY';
import {Store} from '@gongxigames/framework/core/fragments/store/Store';
import {Texture, Circle, Rectangle, IHitArea} from 'pixi.js';

const HIT_AREA_PRESETS = {
    round: new Circle(0, 0, 108/2),
    wide: new Rectangle(-156/2, -108/2, 156, 108),
    no_bg: new Circle(0, 0, 60/2),
} as const;

type BaseButtonProps = CanBeSignals<{
    icon?: string;
    bg?: string;
    disabled?: boolean;
    disabled_alpha?: number;
    action?: () => void;
    hitArea: IHitArea | keyof typeof HIT_AREA_PRESETS;
}>;

export type ButtonRubyProps = Omit<ContainerProps, 'hitArea'> & BaseButtonProps;

export const $ButtonRuby: FC<ButtonRubyProps> = (props) => {
    const name = Store.computed(() => getProp(props.name) ?? getProp(props.icon) ?? 'ButtonRuby');
    const alpha = Store.computed(() => getProp(props.disabled) ? getProp(props.disabled_alpha, 0.28) : 1);
    const bgPressed = Store.computed(() => {
        const bg = getProp(props.bg);

        return bg ? `${bg}_pressed_sys_ui` : Texture.EMPTY;
    });
    const bgFocused = Store.computed(() => {
        const bg = getProp(props.bg);

        return bg ? `${bg}_focused_sys_ui` : Texture.EMPTY;
    });
    const bgNormal = Store.computed(() => {
        const bg = getProp(props.bg);

        return bg ? `${bg}_normal_sys_ui` : Texture.EMPTY;
    });
    const iconPressed = mapProp(props.icon, icon => icon ? `${icon}_sys_ui` : Texture.EMPTY);
    const iconFocused = mapProp(props.icon, icon => icon ? `${icon}_hover_sys_ui` : Texture.EMPTY);
    const iconNormal = mapProp(props.icon, icon => icon ? `${icon}_sys_ui` : Texture.EMPTY);
    const hitArea = mapProp(props.hitArea, hitArea => typeof hitArea === 'string' ? HIT_AREA_PRESETS[hitArea] : hitArea);

    return <$Button
        {...props}
        name={name}
        hitArea={hitArea}
    >{(props) => {
        return <$SetVisibility selector={props.selector}>
            <$Sprite name='pressed' anchor={0.5} texture={bgPressed}>
                <$Sprite scale={0.94} anchor={0.5} texture={iconPressed}/>
            </$Sprite>

            <$Sprite name='focused' anchor={0.5} texture={bgFocused}>
                <$Sprite anchor={0.5} texture={iconFocused}/>
            </$Sprite>

            <$Sprite name='normal' anchor={0.5} texture={bgNormal}>
                <$Sprite anchor={0.5} texture={iconNormal}/>
            </$Sprite>

            <$Sprite alpha={alpha} name='disabled' anchor={0.5} texture={bgNormal}>
                <$Sprite anchor={0.5} texture={iconNormal}/>
            </$Sprite>
        </$SetVisibility>
    }}</$Button>
};